import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { grey } from '../../../colors'

export interface FormSelectOption {
  label: string
  value: string | boolean
  /** Optional helper shown under the label in the dropdown (not in the closed control). */
  description?: string
}

interface FormSelectProps {
  name: string
  label: string
  options: FormSelectOption[]
  required?: boolean
  disabled?: boolean
  /** When false, the caller renders its own label above (keeps labels consistent across fields). */
  showLabel?: boolean
  onChange?: (value: string | boolean) => void
}

/**
 * A plain single-select dropdown bound to react-hook-form — matches the CE-38 mockup's
 * Role Type / Report Access / View TA Results controls (chevron, no search box). Options may
 * carry a `description` that renders under the label in the menu; the closed control shows
 * only the selected option's label (via `renderValue`).
 */
const FormSelect: React.FC<FormSelectProps> = ({ name, label, options, required = false, disabled = false, showLabel = true, onChange }) => {
  const { control } = useFormContext()
  const labelId = `${name}-label`
  const labelFor = (value: unknown): string => options.find((o) => o.value === value)?.label ?? ''

  return (
    <FormControl fullWidth size="small" required={required}>
      {showLabel && (
        <InputLabel id={labelId} sx={{ position: 'static', transform: 'none', mb: 1, color: grey[250], fontWeight: 600 }} shrink={false}>
          {label}
        </InputLabel>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            labelId={showLabel ? labelId : undefined}
            aria-label={label}
            value={field.value ?? ''}
            disabled={disabled}
            IconComponent={KeyboardArrowDownIcon}
            sx={{ backgroundColor: grey[0] }}
            renderValue={(value) => labelFor(value)}
            onChange={(event) => {
              field.onChange(event)
              onChange?.(event.target.value as string | boolean)
            }}
          >
            {options.map((option) => (
              // MUI MenuItem's value type rejects boolean; the TA Yes/No option is boolean at
              // runtime (MUI matches it fine). Cast to satisfy the type without using `any`.
              <MenuItem key={String(option.value)} value={option.value as unknown as string} sx={{ whiteSpace: 'normal' }}>
                <Box>
                  <Typography fontSize="14px" fontWeight={500} color={grey[250]}>{option.label}</Typography>
                  {option.description != null && (
                    <Typography fontSize="12px" color={grey[850]}>{option.description}</Typography>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        )}
      />
    </FormControl>
  )
}

export default FormSelect
